import { useState, useEffect } from 'react'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@cutilei/ui'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import Select from 'components/Inputs/Select'
import MainButton from 'components/Buttons/MainButton'
import submitOnEnter from 'utils/submitOnEnter'
import bankAccountValidator from 'validators/bankAccountValidator'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { Formik } from 'formik'

import {
  Form,
  FormField,
  Input,
  Row,
  InfoText,
  WarningText,
  ErrorContainer
} from './styles'

export default function EditBankAccountModal ({
  visible,
  data: bankAccount,
  onConfirm: handleConfirm,
  onClose: handleClose
}) {
  const [loading, setLoading] = useState (true)
  const [banks, setBanks] = useState (null)
  const [errorMessage, setErrorMessage] = useState (null)
  const businessId = AuthService.getBusinessId ()
  const token = AuthService.getToken ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    if (visible) getBanks ()
  }, [visible])

  const getBanks = async () => {
    try {
      setLoading (true)
      const { data: banks } = await cutileiApi.get ('/banks', requestConfig)

      setBanks (banks.map (bank => ({
        label: `(${bank.code}) ${bank.name}`,
        value: bank
      })))
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='md' closeOnOutsideClick={false} aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Editar conta bancária</DialogTitle>
        </DialogHeader>
        {loading ? <DialogBody><Loading/></DialogBody> : (
          <Formik
            validationSchema={bankAccountValidator}
            initialValues={{
              'bank': {
                label: `(${bankAccount.bank.code}) ${bankAccount.bank.name}`,
                value: bankAccount.bank
              },
              'description': bankAccount.description
            }}
            onSubmit={async (values, { setSubmitting }) => {
              const { bank, description } = values

              try {
                const { data: editedBankAccount } = await cutileiApi.put (
                  `/businesses/${businessId}/bank_accounts/${bankAccount.id}`,
                  {bankId: bank.value.id, description},
                  requestConfig
                )

                setSubmitting (false)
                handleConfirm (editedBankAccount)
                handleClose ()
              } catch (error) {
                setErrorMessage (getApiErrorMessage (error))
                setSubmitting (false)
                logApiError (error)
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              isSubmitting,
              setFieldValue,
              setFieldTouched,
              handleChange,
              handleBlur,
              handleSubmit
            }) => {
              return (
                <>
                  <DialogBody>
                    <Form onKeyDown={submitOnEnter(handleSubmit, isSubmitting)}>
                      <Row>
                        <FormField>
                        <Select
                            name='bank'
                            placeholder='Banco...'
                            value={values.bank}
                            options={banks}
                            isClearable={true}
                            onChange={setFieldValue}
                            onBlur={setFieldTouched}
                            error={errors.bank}
                            touched={touched.bank}
                          />
                          {touched.bank && errors.bank && (
                            <WarningText>
                              {errors.bank}
                            </WarningText>
                          )}
                        </FormField>
                      </Row>
                      <Row>
                        <FormField>
                          <Input 
                            placeholder='Descrição'
                            value={values.description}
                            onChange={handleChange ('description')} 
                            onBlur={handleBlur ('description')}
                          />
                          {touched.description && errors.description && (
                            <WarningText>
                              {errors.description}
                            </WarningText>
                          )}
                        </FormField>
                      </Row>
                      {errorMessage && (
                        <ErrorContainer>
                          <InfoText>{errorMessage}</InfoText>
                        </ErrorContainer>
                      )}
                    </Form>
                  </DialogBody>
                  <DialogFooter>
                    <MainButton
                      label='Salvar alterações'
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      loading={isSubmitting}
                      width={180}
                    />
                  </DialogFooter>
                </>
              )
            }}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  )
}
